
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type LiquidSurfaceShaderUniforms = {
	uModelMatrix: WebGLUniformLocation,
	uModelViewMatrix: WebGLUniformLocation,
	uProjectionMatrix: WebGLUniformLocation,
	uTime: WebGLUniformLocation,
	uAmplitude: WebGLUniformLocation,
	uWaveSpeed: WebGLUniformLocation,
	uWaveDirection: WebGLUniformLocation,
	uShoreFadeDistance: WebGLUniformLocation,
	uLiquidColor: WebGLUniformLocation,
	uOpacity: WebGLUniformLocation,
	uAmbientColor: WebGLUniformLocation,
	uCameraPosition: WebGLUniformLocation,
	uGlintRoughness: WebGLUniformLocation,
	uScreenSize: WebGLUniformLocation,
	uVolumetricMap: WebGLUniformLocation,
	uSceneDepth: WebGLUniformLocation,
	uInvViewProjection: WebGLUniformLocation,
	uShoreDepthFade: WebGLUniformLocation,
	uTextureSampler: WebGLUniformLocation,
	uLightDataMap: WebGLUniformLocation,
	uLightCount: WebGLUniformLocation,
	uLightDataHeight: WebGLUniformLocation,
	uTileDataMap: WebGLUniformLocation,
	uTilesX: WebGLUniformLocation,
	uTileSize: WebGLUniformLocation,
	uShadowCascadeMap0: WebGLUniformLocation,
	uShadowCascadeMap1: WebGLUniformLocation,
	uShadowCascadeMap2: WebGLUniformLocation,
	uShadowCascadeMap3: WebGLUniformLocation,
	uCascadeViewMatrix: WebGLUniformLocation[],
	uCascadeSplits: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation
};

export type LiquidSurfaceShaderAttributes = {
	aVertexPosition: number,
	aTextureCoord: number,
	aEdgeDistance: number,
	vWorldPosition: number,
	vWorldNormal: number,
	vTextureCoord: number,
	vEdgeDistance: number
};

export type LiquidSurfaceShaderSettings = ('VOLUMETRIC' | 'DEPTH_SHORE_FADE' | 'USE_TEXTURE' | 'TILE_SKIP_SHADOWED' | 'SHADOW_CSM')[];

export class LiquidSurfaceShader extends ShaderBase<LiquidSurfaceShaderUniforms, LiquidSurfaceShaderAttributes> {

    private static fragmentShaderSource = `
in vec4 vWorldPosition;
in vec3 vWorldNormal;
in vec2 vTextureCoord;
in float vEdgeDistance;
out vec4 fragColor;

uniform vec4 uLiquidColor;
uniform float uOpacity;
uniform vec3 uAmbientColor;
uniform vec3 uCameraPosition;
uniform float uShoreFadeDistance;
uniform float uGlintRoughness;

#if defined(NOODLE_VOLUMETRIC) || defined(NOODLE_DEPTH_SHORE_FADE)
uniform vec2 uScreenSize;
#endif
#ifdef NOODLE_VOLUMETRIC
uniform sampler2D uVolumetricMap;
#endif
#ifdef NOODLE_DEPTH_SHORE_FADE
uniform sampler2D uSceneDepth;
uniform mat4 uInvViewProjection;
uniform float uShoreDepthFade;
// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---
#endif
#ifdef NOODLE_USE_TEXTURE
uniform sampler2D uTextureSampler;
#endif

// --- begin: pbr_lighting.glsl ---
// Cook-Torrance PBR BRDF
// D: GGX/Trowbridge-Reitz normal distribution
// F: Schlick fresnel approximation
// G: Smith geometry (Schlick-GGX)

float distribution_ggx(float NdotH, float roughness) {
    float a = roughness * roughness;
    float a2 = a * a;
    float denom = NdotH * NdotH * (a2 - 1.0) + 1.0;
    return a2 / (3.14159265 * denom * denom);
}

float geometry_schlick_ggx(float NdotV, float roughness) {
    float r = roughness + 1.0;
    float k = (r * r) / 8.0;
    return NdotV / (NdotV * (1.0 - k) + k);
}

float geometry_smith(float NdotV, float NdotL, float roughness) {
    return geometry_schlick_ggx(NdotV, roughness) * geometry_schlick_ggx(NdotL, roughness);
}

vec3 fresnel_schlick(float cosTheta, vec3 F0) {
    return F0 + (1.0 - F0) * pow(clamp(1.0 - cosTheta, 0.0, 1.0), 5.0);
}

vec3 pbr_direct_lighting_ndotl(vec3 normal, vec3 surfaceToLight, vec3 surfaceToCamera, vec3 albedo, float metallic, float roughness, vec3 lightColor, float NdotL) {
    vec3 H = normalize(surfaceToLight + surfaceToCamera);

    float NdotV = max(dot(normal, surfaceToCamera), 0.001);
    float NdotH = max(dot(normal, H), 0.0);
    float HdotV = max(dot(H, surfaceToCamera), 0.0);

    // Dielectrics reflect ~4% at normal incidence; metals use their albedo as F0
    vec3 F0 = mix(vec3(0.04), albedo, metallic);

    float D = distribution_ggx(NdotH, roughness);
    float G = geometry_smith(NdotV, NdotL, roughness);
    vec3 F = fresnel_schlick(HdotV, F0);

    // Specular: Cook-Torrance
    vec3 specular = (D * G * F) / max(4.0 * NdotV * NdotL, 0.001);

    // Diffuse: energy-conserving Lambert (metals have no diffuse)
    // Energy conservation: metals have no diffuse, dielectrics scale by (1 - F).
    // PI divisor intentionally omitted — standard game engine simplification to
    // avoid dimming the scene and re-tuning all light intensities.
    vec3 kD = (vec3(1.0) - F) * (1.0 - metallic);
    vec3 diffuse = kD * albedo;

    return (diffuse + specular) * lightColor * NdotL;
}

vec3 pbr_direct_lighting(vec3 normal, vec3 surfaceToLight, vec3 surfaceToCamera, vec3 albedo, float metallic, float roughness, vec3 lightColor) {
    float NdotL = max(dot(normal, surfaceToLight), 0.0);
    return pbr_direct_lighting_ndotl(normal, surfaceToLight, surfaceToCamera, albedo, metallic, roughness, lightColor, NdotL);
}

// --- end: pbr_lighting.glsl ---
// --- begin: tile_light_loop.glsl ---
// Tile-binned light loop shared between the forward (transparent / liquid)
// pipeline and the deferred batched-lighting pass. Returns ONLY the direct
// PBR contribution from tile-assigned lights; ambient is the caller's job.
//
// Compile-time variants:
//
//   NOODLE_TILE_SKIP_SHADOWED — set by the deferred batched-lighting pass.
//     Lights whose d0.w >= 4.0 (shadow flag set) are skipped before fetching
//     d1 — they're already handled by the per-light shadow passes elsewhere
//     in the deferred pipeline. Directional lights are always shadow-flagged
//     in this engine, so the directional branch is compiled out as well.
//
//   (default) — forward path. The shadow flag is stripped via mod() so
//     shadow-flagged lights still contribute. Directional lights take a
//     dedicated branch (no distance cull, CSM applied when NOODLE_SHADOW_CSM
//     is defined). Shadow point/spot lights run unshadowed in forward — the
//     forward pipeline has no per-light shadow pass.
//
// Light data layout (4 RGBA texels per light) matches Light.packGpuData:
//   d0 = position.xyz, type     (type += 4.0 when the light casts shadows)
//   d1 = color.rgb,    outerRange²
//   d2 = direction.xyz, _       (directional / spot only)
//   d3 = innerRange, outerRange, cos(innerCone), cos(outerCone)
//
// Caller is responsible for including pbr_lighting.glsl before this file —
// the deferred shader's per-light pass also needs it, so it stays at the
// caller scope to avoid double-inclusion.

uniform sampler2D uLightDataMap;
uniform int uLightCount;
uniform float uLightDataHeight;
uniform sampler2D uTileDataMap;
uniform int uTilesX;
uniform int uTileSize;

// Hard cap per tile — must match MAX_LIGHTS_PER_TILE in TileLightAssigner.ts.
// The tile data texture row stride is (1 + this), so changing it on one side
// without the other shifts the texelFetch indices and corrupts every lookup.
#define NOODLE_MAX_LIGHTS_PER_TILE 128

#if !defined(NOODLE_TILE_SKIP_SHADOWED) && defined(NOODLE_SHADOW_CSM)
// --- begin: csm_shadow.glsl ---
uniform sampler2D uShadowCascadeMap0;
uniform sampler2D uShadowCascadeMap1;
uniform sampler2D uShadowCascadeMap2;
uniform sampler2D uShadowCascadeMap3;
uniform mat4 uCascadeViewMatrix[4];
uniform vec4 uCascadeSplits;
uniform mat4 uViewMatrix;

// Positive-warp EVSM constant. Must match the value baked into the moments
// resolve shader (\`csm-moments-resolve-fragment.glsl\`) and the temporal
// blend shader (\`csm-moments-blend-fragment.glsl\`). 5.0 is the canonical
// value for fp32 moments — keeps the exponential within precision limits
// while damping the per-blur-kernel variance signal that c=7 was inflating.
// Higher c tightens Chebyshev at silhouettes but amplifies blur-kernel
// variance on glancing surfaces, producing visible per-shadow-texel
// quantization (square cells of differing shadow value) on near-vertical
// walls viewed at low sun. c=5 trades that off for slightly more bleed
// at shadow-on-shadow contacts.
//
// Future high-quality option: full EVSM with both positive and negative
// warps (RGBA32F, 2× memory, ~50% more resolve/blur/blend cost). Stores
// \`(M1+, M2+, M1-, M2-) = (exp(c·d), exp(2c·d), -exp(-c·d), exp(-2c·d))\`
// and takes \`min(chebyshev+, chebyshev-)\` — the negative bound is tight
// where the positive is loose and vice versa, almost completely
// eliminating shadow-on-shadow halos. Skipped for now to keep 60fps on
// mid-tier hardware; gate behind a high/ultra graphics preset if added.
#define C_EVSM 5.0

// Chebyshev variance floor — caps \`1/variance\` blow-up on near-constant
// regions. Tuned in the warped domain; the value is in "exp(c·d) units"
// squared so it scales with C_EVSM. 1e-4 is the canonical Lauritzen value
// scaled into the warped range.
#define MIN_VARIANCE 0.0001

// Light-bleed reduction crush. linstep pushes the lower tail of the
// Chebyshev bound to zero so faint glows under arches / behind silhouettes
// read as fully shadowed instead of "lit at p≈0.15". Tradeoff: also crushes
// genuine penumbra — 0.2 is a moderate setting; bump to 0.3 if Sponza shows
// visible bleed under arches.
#define LIGHT_BLEED_THRESHOLD 0.3

// Slope-scaled receiver bias. On surfaces grazing the light (NdotL near 0)
// the moments blur kernel mixes shadow-map texels with widely different
// recorded depths — \`M2 - M1²\` becomes large enough that Chebyshev returns
// per-texel intermediate p values, then \`linstep(LIGHT_BLEED_THRESHOLD, 1.0, p)\`
// crushes those into visible cell boundaries on the receiver wall. This
// pushes the warped receiver depth toward the light proportionally to
// \`(1 - NdotL)\`, so grazing receivers fall clearly below the moment mean
// (Chebyshev's early-out → 1.0) instead of grazing the variance threshold.
//
// Value is in NDC-z units; with cascade 0's ~115m Z-range, 0.01 ≈ ~115cm
// world detach at the most-grazing case. Surfaces facing the sun directly
// (NdotL=1) get no bias at all, so player shadows on flat ground at high
// sun stay attached. The artifact is most visible at low sun where many
// surfaces are grazing — those same surfaces benefit most from the bias.
// Smaller values (0.002-0.004) leave residual cell artifacts on extremely
// grazing walls; 0.01 is large enough to clear the moments-kernel variance
// range across all cascades while keeping the most-grazing detach under 1m
// (acceptable since players don't notice missing shadows on near-vertical
// surfaces — only on flat ground where bias is zero).
//
// Per-cascade scaling note: a previous attempt scaled this by
// \`cascade0_ZRange / cascade_i_ZRange\` to normalize world-space detach
// across cascades. That dropped cascades 1-3 below the moments-variance
// floor and reintroduced EVSM blob artifacts on distant terrain. The
// variance floor is set by \`texel_size_world / cascade_Z_range\`, which is
// roughly constant across cascades — so the same NDC bias is needed
// regardless of Z-range. Peter-Panning at cascade 3 distance is a
// separate problem that can't be fixed by reducing the bias.
#define SLOPE_BIAS_SCALE 0.01

float linstep(float lo, float hi, float v) {
	return clamp((v - lo) / (hi - lo), 0.0, 1.0);
}

float chebyshev(vec2 moments, float warpedReceiver) {
	if (warpedReceiver <= moments.x) return 1.0;
	float variance = max(moments.y - moments.x * moments.x, MIN_VARIANCE);
	float diff = warpedReceiver - moments.x;
	float pMax = variance / (variance + diff * diff);
	return linstep(LIGHT_BLEED_THRESHOLD, 1.0, pMax);
}

float sample_cascade(sampler2D cascadeMap, vec3 worldPosition, mat4 cascadeMatrix, float slopeBias) {
	vec4 shadowCoord = cascadeMatrix * vec4(worldPosition, 1.0);
	vec3 projectedCoord = shadowCoord.xyz / shadowCoord.w;
	projectedCoord = projectedCoord * 0.5 + 0.5;

	if (projectedCoord.x < 0.0 || projectedCoord.x > 1.0 ||
		projectedCoord.y < 0.0 || projectedCoord.y > 1.0) {
		return 1.0;
	}

	// Hardware bilinear lookup on the pre-blurred moments texture — replaces
	// the 4-tap Poisson PCF kernel. Blurring in moments space (separable
	// Gaussian, applied after resolve) preserves Chebyshev's bound, so the
	// soft-shadow look comes for free without runtime kernel taps.
	vec2 moments = texture(cascadeMap, projectedCoord.xy).rg;
	float warpedReceiver = exp(C_EVSM * (projectedCoord.z - slopeBias));
	return chebyshev(moments, warpedReceiver);
}

float compute_shadow_csm(vec3 worldPosition, float NdotL) {
	float slopeBias = SLOPE_BIAS_SCALE * (1.0 - clamp(NdotL, 0.0, 1.0));
	float viewDepth = (uViewMatrix * vec4(worldPosition, 1.0)).z;

	// Long-shadow overlay: cascade 3 covers mountain-scale casters that the
	// near cascades' tight ortho frustums clip out. Without this, a receiver
	// in cascade 0/1's range under a distant mountain reads "no caster
	// recorded → lit" and the mountain shadow simply disappears as you walk
	// closer. min() against the local cascade keeps near-shadow precision
	// intact while picking up the long-range occluder. Cascade 3 returns 1.0
	// when the receiver projects outside its UV, so receivers far beyond the
	// far cascade fall back to the local result automatically.
	//
	// Cascade 3's per-texel ringing on grazing terrain that contaminated
	// every receiver before is now handled by the slope-scaled receiver
	// bias above (\`slopeBias\`); without that fix, sampling cascade 3
	// unconditionally would re-introduce the speckle.
	float longShadow = sample_cascade(uShadowCascadeMap3, worldPosition, uCascadeViewMatrix[3], slopeBias);

	// Determine cascade and blend between adjacent cascades near boundaries.
	// Inner blend zones use a fixed 5m to keep close-cascade transitions tight;
	// the 2→3 transition uses a wider zone because cascade 3 covers much larger
	// world distances (~2km) so a 5m blend would be invisible at that scale.
	float blendZone = 5.0;

	if (viewDepth > -uCascadeSplits.x) {
		float shadow0 = sample_cascade(uShadowCascadeMap0, worldPosition, uCascadeViewMatrix[0], slopeBias);
		// Blend near the boundary with cascade 1
		float distToBoundary = -viewDepth - (uCascadeSplits.x - blendZone);
		if (distToBoundary > 0.0) {
			float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
			return min(longShadow, mix(shadow0, shadow1, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow0);
	} else if (viewDepth > -uCascadeSplits.y) {
		float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
		// Blend near the boundary with cascade 2
		float distToBoundary = -viewDepth - (uCascadeSplits.y - blendZone);
		if (distToBoundary > 0.0) {
			float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
			return min(longShadow, mix(shadow1, shadow2, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow1);
	} else if (viewDepth > -uCascadeSplits.z) {
		float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
		// Blend near the boundary with cascade 3 (mountain-scale).
		float c23BlendZone = 25.0;
		float distToBoundary = -viewDepth - (uCascadeSplits.z - c23BlendZone);
		if (distToBoundary > 0.0) {
			return min(longShadow, mix(shadow2, longShadow, smoothstep(0.0, 1.0, distToBoundary / c23BlendZone)));
		}
		return min(longShadow, shadow2);
	} else if (viewDepth > -uCascadeSplits.w) {
		// Fade shadow out toward the cascade 3 far boundary so the transition to
		// the unshadowed beyond-CSM region is gradual rather than a hard line.
		// Wide fade zone matches the far-cascade scale.
		float fadeZone = 200.0;
		float distToFar = -viewDepth - (uCascadeSplits.w - fadeZone);
		if (distToFar > 0.0) {
			return mix(longShadow, 1.0, smoothstep(0.0, 1.0, distToFar / fadeZone));
		}
		return longShadow;
	}

	return 1.0; // beyond last cascade
}

// --- end: csm_shadow.glsl ---
#endif

vec3 compute_tile_lighting(vec3 position, vec3 normal, vec3 surfaceToCamera, float metallic, float roughness, vec3 albedo) {
	vec3 lighting = vec3(0.0);

	ivec2 tileCoord = ivec2(gl_FragCoord.xy) / uTileSize;
	int tileIndex = tileCoord.y * uTilesX + tileCoord.x;
	int tileCount = min(int(texelFetch(uTileDataMap, ivec2(0, tileIndex), 0).r), NOODLE_MAX_LIGHTS_PER_TILE);

	for (int t = 0; t < tileCount; t++) {
		int i = int(texelFetch(uTileDataMap, ivec2(t + 1, tileIndex), 0).r);
		vec4 d0 = texelFetch(uLightDataMap, ivec2(0, i), 0);

#ifdef NOODLE_TILE_SKIP_SHADOWED
		// Shadow-casting lights are handled by the per-light shadow passes;
		// skip before d1 fetch to save a texelFetch per culled light.
		if (d0.w >= 4.0) continue;
		float lightType = d0.w;
#else
		// Strip the shadow flag so shadow-flagged lights still contribute.
		// Only the directional CSM branch below applies an actual shadow
		// factor — point/spot shadow lights run unshadowed in forward.
		float lightType = mod(d0.w, 4.0);
#endif

		vec4 d1 = texelFetch(uLightDataMap, ivec2(1, i), 0);

		vec3 lightPos = d0.xyz;
		vec3 delta = lightPos - position;
		float dist2 = dot(delta, delta);
		float outerRange2 = d1.w;
#ifdef NOODLE_TILE_SKIP_SHADOWED
		if (dist2 > outerRange2) continue;
#else
		// Directional has effectively infinite range — keep it.
		if (lightType != 1.0 && dist2 > outerRange2) continue;
#endif

		vec3 lightColor = d1.rgb;
		vec3 surfaceToLight;
		float attenuation = 1.0;
		float shadow = 1.0;
		float NdotL;

#ifndef NOODLE_TILE_SKIP_SHADOWED
		if (lightType == 1.0) {
			vec4 d2 = texelFetch(uLightDataMap, ivec2(2, i), 0);
			surfaceToLight = -normalize(d2.xyz);
			NdotL = dot(normal, surfaceToLight);
			if (NdotL <= 0.0) continue;
#ifdef NOODLE_SHADOW_CSM
			shadow = compute_shadow_csm(position, NdotL);
#endif
		} else
#endif
		{
			surfaceToLight = normalize(delta);
			NdotL = dot(normal, surfaceToLight);
			if (NdotL <= 0.0) continue;

			float dist = sqrt(dist2);
			vec4 d3 = texelFetch(uLightDataMap, ivec2(3, i), 0);
			float innerRange = d3.x;
			float outerRange = d3.y;
			attenuation = smoothstep(outerRange, innerRange, dist);

			if (lightType == 2.0) {
				vec4 d2 = texelFetch(uLightDataMap, ivec2(2, i), 0);
				// surfaceToLight = normalize(lightPos - position), so the
				// surface-from-light direction is -surfaceToLight — already
				// normalized and in scope, no need to renormalize delta.
				float dotFromDir = dot(-surfaceToLight, normalize(d2.xyz));
				attenuation = min(attenuation, smoothstep(d3.w, d3.z, dotFromDir));
			}
		}

		lighting += shadow * attenuation * pbr_direct_lighting_ndotl(normal, surfaceToLight, surfaceToCamera, albedo, metallic, roughness, lightColor, NdotL);
	}

	return lighting;
}

// --- end: tile_light_loop.glsl ---

void main(void) {
	vec4 albedo = uLiquidColor;

#ifdef NOODLE_USE_TEXTURE
	albedo *= texture(uTextureSampler, vTextureCoord);
#endif

	vec3 normal = normalize(vWorldNormal);
	if (!gl_FrontFacing) normal = -normal;
	vec3 position = vWorldPosition.xyz;
	vec3 surfaceToCamera = normalize(uCameraPosition - position);

	// Fresnel effect — more opaque at grazing angles
	float fresnel = pow(1.0 - max(dot(normal, surfaceToCamera), 0.0), 3.0);

	// Water is a dielectric (metallic = 0); roughness drives the per-light
	// specular glint sharpness. Lower = pin-point sun glare, higher = broad
	// soft highlight. Wave normals (analytical from sine derivatives in the
	// vertex shader) give the per-pixel "glistering" as waves move.
	vec3 lighting = uAmbientColor * albedo.rgb + compute_tile_lighting(position, normal, surfaceToCamera, 0.0, uGlintRoughness, albedo.rgb);

	// Additive fresnel sky-reflection tint. Additive (not mix) preserves the
	// water's albedo colour — the surface still reads as "blue water", just
	// with a brighter glaze at grazing angles.
	vec3 skyReflection = uAmbientColor * 1.5 * mix(vec3(1.0), albedo.rgb, 0.35);
	lighting += skyReflection * fresnel;

#ifdef NOODLE_VOLUMETRIC
	lighting += texture(uVolumetricMap, gl_FragCoord.xy / uScreenSize).rgb;
#endif

	float finalAlpha = albedo.a * uOpacity * mix(1.0, 1.5, fresnel);
	// Floor each shore-fade so the edge of the water reads as a visible line
	// instead of fading to invisibility — without this, shorelines look like
	// thinning fog rather than a defined water boundary.
	const float SHORE_ALPHA_FLOOR = 0.55;
	if (uShoreFadeDistance > 0.0) {
		finalAlpha *= max(SHORE_ALPHA_FLOOR, smoothstep(0.0, uShoreFadeDistance * 0.5, vEdgeDistance));
	}
#ifdef NOODLE_DEPTH_SHORE_FADE
	// Scene-depth shore fade: reconstruct the underlying opaque surface's world
	// position from the G-buffer depth and fade alpha where it is close to (or
	// above) the water plane. Hides sloppy polygon overshoot at walls, boulders,
	// and terrain. Reversed-Z cleared depth (= 0.0) means "no opaque surface drawn"
	// (sky / empty scene); skip the fade so water stays visible in an empty editor scene.
	vec2 sceneUV = gl_FragCoord.xy / uScreenSize;
	float sceneDepth = texture(uSceneDepth, sceneUV).r;
	if (sceneDepth > 0.0) {
		vec3 scenePos = worldPosFromDepth(sceneDepth, sceneUV, uInvViewProjection);
		float depthDelta = position.y - scenePos.y;
		finalAlpha *= max(SHORE_ALPHA_FLOOR, smoothstep(0.0, uShoreDepthFade, depthDelta));
	}
#endif
	finalAlpha = clamp(finalAlpha, 0.0, 1.0);
	vec3 finalColor = lighting;

	// Premultiplied alpha output
	fragColor = vec4(finalColor * finalAlpha, finalAlpha);
}
`;

    private static vertexShaderSource = `
layout(location = 0) in vec3 aVertexPosition;
layout(location = 2) in vec2 aTextureCoord;
layout(location = 3) in float aEdgeDistance;

uniform mat4 uModelMatrix;
uniform mat4 uModelViewMatrix;
uniform mat4 uProjectionMatrix;
uniform float uTime;
uniform float uAmplitude;
uniform float uWaveSpeed;
uniform vec2 uWaveDirection;
uniform float uShoreFadeDistance;

out vec4 vWorldPosition;
out vec3 vWorldNormal;
out vec2 vTextureCoord;
out float vEdgeDistance;

// Rotate a 2D direction by an angle in radians
vec2 rotateDir(vec2 dir, float angle) {
	float c = cos(angle);
	float s = sin(angle);
	return vec2(dir.x * c - dir.y * s, dir.x * s + dir.y * c);
}

void main(void) {
	vec3 pos = aVertexPosition;

	// World position of the vertex (before wave displacement) for wave calculation
	vec3 worldPos = (uModelMatrix * vec4(pos, 1.0)).xyz;

	float totalDisplacement = 0.0;
	float dYdx = 0.0;
	float dYdz = 0.0;

	float baseFreq = 1.5;
	float amp = uAmplitude;
	vec2 dir = normalize(uWaveDirection);

	// 4 octaves of sine waves with increasing frequency and decreasing amplitude
	for (int i = 0; i < 4; i++) {
		float freq = baseFreq * pow(2.0, float(i));
		float currentAmp = amp / pow(2.0, float(i));
		vec2 currentDir = rotateDir(dir, float(i) * 0.52); // ~30 degrees rotation per octave

		float phase = uTime * uWaveSpeed * (1.0 + float(i) * 0.3);
		float d = dot(currentDir, worldPos.xz);
		float wave = sin(d * freq + phase);
		float waveDerivative = cos(d * freq + phase) * freq;

		totalDisplacement += currentAmp * wave;
		dYdx += currentAmp * waveDerivative * currentDir.x;
		dYdz += currentAmp * waveDerivative * currentDir.y;
	}

	// Shore fade: attenuate wave displacement + slope as we approach the shoreline.
	float shoreFade = uShoreFadeDistance > 0.0
		? smoothstep(0.0, uShoreFadeDistance, aEdgeDistance)
		: 1.0;
	totalDisplacement *= shoreFade;
	dYdx *= shoreFade;
	dYdz *= shoreFade;

	// Displace Y in local space
	pos.y += totalDisplacement;

	// Analytical normal from partial derivatives
	vec3 localNormal = normalize(vec3(-dYdx, 1.0, -dYdz));

	vWorldPosition = uModelMatrix * vec4(pos, 1.0);
	vWorldNormal = normalize(mat3(uModelMatrix) * localNormal);
	vTextureCoord = aTextureCoord;
	vEdgeDistance = aEdgeDistance;

	vec4 viewPos = uModelViewMatrix * vec4(pos, 1.0);
	gl_Position = uProjectionMatrix * viewPos;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: LiquidSurfaceShaderUniformsContainer;
    public attributesContainer: LiquidSurfaceShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: LiquidSurfaceShaderSettings) {
        super(ctx, LiquidSurfaceShader.fragmentShaderSource, LiquidSurfaceShader.vertexShaderSource, settings);
        this.uniformsContainer = new LiquidSurfaceShaderUniformsContainer(ctx, this);
        this.attributesContainer = new LiquidSurfaceShaderShaderAttributesContainer(ctx, this);
    }
}

class LiquidSurfaceShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: LiquidSurfaceShader) {
        super(ctx);
    }

    public setModelMatrix(value: mat4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uModelMatrix', this.shader.uniforms.uModelMatrix, value, 'mat4');
        return this;
    }

    public setModelViewMatrix(value: mat4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setProjectionMatrix(value: mat4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setTime(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uTime', this.shader.uniforms.uTime, value, 'float');
        return this;
    }

    public setAmplitude(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uAmplitude', this.shader.uniforms.uAmplitude, value, 'float');
        return this;
    }

    public setWaveSpeed(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uWaveSpeed', this.shader.uniforms.uWaveSpeed, value, 'float');
        return this;
    }

    public setWaveDirection(value: vec2 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uWaveDirection', this.shader.uniforms.uWaveDirection, value, 'vec2');
        return this;
    }

    public setShoreFadeDistance(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uShoreFadeDistance', this.shader.uniforms.uShoreFadeDistance, value, 'float');
        return this;
    }

    public setLiquidColor(value: vec4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uLiquidColor', this.shader.uniforms.uLiquidColor, value, 'vec4');
        return this;
    }

    public setOpacity(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uOpacity', this.shader.uniforms.uOpacity, value, 'float');
        return this;
    }

    public setAmbientColor(value: vec3 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uAmbientColor', this.shader.uniforms.uAmbientColor, value, 'vec3');
        return this;
    }

    public setCameraPosition(value: vec3 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uCameraPosition', this.shader.uniforms.uCameraPosition, value, 'vec3');
        return this;
    }

    public setGlintRoughness(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uGlintRoughness', this.shader.uniforms.uGlintRoughness, value, 'float');
        return this;
    }

    public setScreenSize(value: vec2 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uScreenSize', this.shader.uniforms.uScreenSize, value, 'vec2');
        return this;
    }

    public setVolumetricMap(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uVolumetricMap', this.shader.uniforms.uVolumetricMap, texture, 'sampler2D');
        return this;
    }

    public setSceneDepth(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uSceneDepth', this.shader.uniforms.uSceneDepth, texture, 'sampler2D');
        return this;
    }

    public setInvViewProjection(value: mat4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uInvViewProjection', this.shader.uniforms.uInvViewProjection, value, 'mat4');
        return this;
    }

    public setShoreDepthFade(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uShoreDepthFade', this.shader.uniforms.uShoreDepthFade, value, 'float');
        return this;
    }

    public setTextureSampler(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uTextureSampler', this.shader.uniforms.uTextureSampler, texture, 'sampler2D');
        return this;
    }

    public setLightDataMap(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uLightDataMap', this.shader.uniforms.uLightDataMap, texture, 'sampler2D');
        return this;
    }

    public setLightCount(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uLightCount', this.shader.uniforms.uLightCount, value, 'int');
        return this;
    }

    public setLightDataHeight(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uLightDataHeight', this.shader.uniforms.uLightDataHeight, value, 'float');
        return this;
    }

    public setTileDataMap(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uTileDataMap', this.shader.uniforms.uTileDataMap, texture, 'sampler2D');
        return this;
    }

    public setTilesX(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uTilesX', this.shader.uniforms.uTilesX, value, 'int');
        return this;
    }

    public setTileSize(value: number) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uTileSize', this.shader.uniforms.uTileSize, value, 'int');
        return this;
    }

    public setShadowCascadeMap0(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap0', this.shader.uniforms.uShadowCascadeMap0, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap1(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap1', this.shader.uniforms.uShadowCascadeMap1, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap2(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap2', this.shader.uniforms.uShadowCascadeMap2, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap3(texture: Texture) : LiquidSurfaceShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap3', this.shader.uniforms.uShadowCascadeMap3, texture, 'sampler2D');
        return this;
    }

    public setCascadeViewMatrix(index: number, value: mat4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        if(index < 0 || index >= 4) throw new Error('Invalid index used for uCascadeViewMatrix: ' + index);
        this.setValue('uCascadeViewMatrix', this.shader.uniforms.uCascadeViewMatrix[index], value, 'mat4');
        return this;
    }

    public setCascadeSplits(value: vec4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uCascadeSplits', this.shader.uniforms.uCascadeSplits, value, 'vec4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : LiquidSurfaceShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uVolumetricMap', uniform: this.shader.uniforms.uVolumetricMap, dataType: 'sampler2D' },
			{ name: 'uSceneDepth', uniform: this.shader.uniforms.uSceneDepth, dataType: 'sampler2D' },
			{ name: 'uTextureSampler', uniform: this.shader.uniforms.uTextureSampler, dataType: 'sampler2D' },
			{ name: 'uLightDataMap', uniform: this.shader.uniforms.uLightDataMap, dataType: 'sampler2D' },
			{ name: 'uTileDataMap', uniform: this.shader.uniforms.uTileDataMap, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap0', uniform: this.shader.uniforms.uShadowCascadeMap0, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap1', uniform: this.shader.uniforms.uShadowCascadeMap1, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap2', uniform: this.shader.uniforms.uShadowCascadeMap2, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap3', uniform: this.shader.uniforms.uShadowCascadeMap3, dataType: 'sampler2D' }
        ];
    }
}

class LiquidSurfaceShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: LiquidSurfaceShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : LiquidSurfaceShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setTextureCoord(settings: { length: number, offset: number, itemSize: number }) : LiquidSurfaceShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setEdgeDistance(settings: { length: number, offset: number, itemSize: number }) : LiquidSurfaceShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aEdgeDistance, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldPosition(settings: { length: number, offset: number, itemSize: number }) : LiquidSurfaceShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldNormal(settings: { length: number, offset: number, itemSize: number }) : LiquidSurfaceShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTextureCoord(settings: { length: number, offset: number, itemSize: number }) : LiquidSurfaceShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvEdgeDistance(settings: { length: number, offset: number, itemSize: number }) : LiquidSurfaceShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vEdgeDistance, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}